import { create } from 'zustand';
import { socketService } from '../services/socketService';

export interface Player {
  name: string;
  isLeader: boolean;
  hasJoined: boolean;
  cards: Card[];
}

export interface Card {
  suit: string;
  value: string;
  faceUp: boolean;
  color: 'red' | 'blue';
}

type GamePhase = 'setup' | 'waiting' | 'playing';
type GameRole = 'leader' | 'member' | null;

interface GameState {
  gamePhase: GamePhase;
  gameRole: GameRole;
  leader: string | null;
  players: Player[];
  currentTurn: number;
  deck: Card[];
  discardPile: Card[];
  currentDealer: string | null;
  allPlayersJoined: boolean;
  
  // Actions
  setGameRole: (role: GameRole) => void;
  setLeader: (name: string) => void;
  addPlayer: (name: string) => void;
  setPlayerJoined: (name: string) => void;
  startGame: () => void;
  setGamePhase: (phase: GamePhase) => void;
  updateCards: (cards: { playerName: string; cards: Card[] }[]) => void;
  setCurrentTurn: (playerIndex: number) => void;
  resetGame: () => void;
  setAllPlayersJoined: () => void;
}

const initialState = {
  gamePhase: 'setup' as GamePhase,
  gameRole: null as GameRole,
  leader: null,
  players: [] as Player[],
  currentTurn: -1,
  deck: [] as Card[],
  discardPile: [] as Card[],
  currentDealer: null,
  allPlayersJoined: false,
};

export const useGameStore = create<GameState>()((set) => ({
  ...initialState,

  setGameRole: (role: GameRole) => set(() => ({ gameRole: role })),

  setLeader: (name: string) => set(() => ({ 
    leader: name, 
    gamePhase: 'waiting',
    currentDealer: name, // Leader is always the first dealer
    players: [{
      name,
      isLeader: true,
      hasJoined: true,
      cards: []
    }]
  })),
  
  addPlayer: (name: string) => set((state) => ({
    players: [
      ...state.players,
      {
        name,
        isLeader: false,
        hasJoined: false,
        cards: []
      }
    ]
  })),

  setPlayerJoined: (name: string) => set((state) => ({
    players: state.players.map((player) =>
      player.name === name ? { ...player, hasJoined: true } : player
    )
  })),

  startGame: () => set((state) => {
    socketService.startGame().catch(console.error);
    return {
      gamePhase: 'playing',
      currentTurn: state.players.findIndex(p => p.name === state.leader)
    };
  }),

  setGamePhase: (phase: GamePhase) => set(() => ({ gamePhase: phase })),

  updateCards: (cards) => set((state) => ({
    players: state.players.map(player => {
      const updatedCards = cards.find(c => c.playerName === player.name);
      return updatedCards ? { ...player, cards: updatedCards.cards } : player;
    })
  })),

  setCurrentTurn: (playerIndex) => set(() => ({ currentTurn: playerIndex })),

  resetGame: () => set(initialState),

  setAllPlayersJoined: () => set(() => ({ allPlayersJoined: true }))
})); 